import React from 'react';

import { formatMessage } from '@99designs/common/i18n';
import { Uppy } from '@uppy/core';
import Field from 'common/components/Field';
import { Matrix, MatrixItem } from 'common/components/Matrix';
import Bugsnag from 'common/utils/Bugsnag';
import { csrfToken } from 'common/utils/xhr';
import qs from 'qs';
import {
    Attachment,
    AttachmentPreview,
    UploaderAttachmentFileInput,
    UploaderAttachmentProgress,
} from './components';
import Dropzone from './Dropzone';
import { createUploader, UploaderConfig, UppyFileWithKey } from './uploader';

export interface Attachment {
    bucket: string;
    name: string;
    key: string;
    previewable: boolean;
    size: number;
}

export interface UploadingAttachment extends Attachment {
    id: string;
    bytesUploaded: number;
}

interface Props {
    id?: string;
    value: Attachment[];
    onChange: (files: Attachment[]) => void;
    uploader: UploaderConfig;
}

interface State {
    uploading: UploadingAttachment[];
    completed: Attachment[];
    errorMessage: string | null;
}

interface UppyUpload {
    id: string;
    fileIDs: string[];
}

class AttachmentUploader extends React.Component<Props, State> {
    public uploader: Uppy;

    static defaultProps: Partial<Props> = {
        value: [],
        uploader: (global as any).uploaderConfig || {},
    };

    constructor(props: Props) {
        super(props);

        // Strip out __typename from File
        this.state = {
            uploading: [],
            completed: this.props.value
                ? this.props.value.map(v => ({
                      bucket: v.bucket,
                      name: v.name,
                      key: v.key,
                      previewable: v.previewable,
                      size: v.size,
                  }))
                : [],
            errorMessage: null,
        };

        this.uploader = createUploader(this.props.uploader);

        this.uploader
            .on('upload', this.handleUpload)
            .on('upload-progress', this.handleProgress)
            .on('upload-success', this.handleUploadSuccess)
            .on('upload-error', this.handleUploadError)
            .on('restriction-failed', this.handleUploadError);

        // TODO Remove this once we're out of Start
        const { successEndpoint, bucket } = this.props.uploader;
        if (successEndpoint != undefined) {
            this.uploader.on('upload-success', async (file: UppyFileWithKey) => {
                try {
                    const response = await fetch(successEndpoint, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded',
                            'X-CSRF-Token': csrfToken(),
                        },
                        body: qs.stringify({
                            key: file.meta.key,
                            name: file.name,
                            bucket: bucket,
                        }),
                    });

                    if (response.status != 200) {
                        Bugsnag.notify('upload success callback failed', {
                            metaData: {
                                response: {
                                    status: response.status,
                                    body: response.body,
                                },
                                file,
                            },
                        });
                        throw new Error('csrfError');
                    }
                } catch (error) {
                    this.handleUploadError(file, error);
                }
            });
        }
    }

    private handleUpload = (upload: UppyUpload) => {
        const attachments: UploadingAttachment[] = [];
        upload.fileIDs.forEach(id => {
            const file = this.uploader.getFile<{ key: string }>(id);
            attachments.push(this.createAttachment(file));
        });
        return this.setState((prev: State) => ({
            uploading: [...prev.uploading, ...attachments],
        }));
    };

    private createAttachment(file: UppyFileWithKey): UploadingAttachment {
        return {
            id: file.id,
            name: file.name,
            key: file.meta.key,
            bucket: this.props.uploader.bucket,
            size: file.size,
            previewable:
                file.extension != undefined &&
                ['jpeg', 'jpg', 'png', 'gif', 'svg'].includes(file.extension.toLowerCase()),
            bytesUploaded: 0,
        };
    }

    private handleProgress = (
        file: UppyFileWithKey,
        progress: { bytesUploaded: number; bytesTotal: number }
    ) => {
        return this.setState((prev: State) => {
            const uploading = prev.uploading.slice();
            const item = uploading.find(a => a.id === file.id);
            if (item) {
                item.bytesUploaded = progress.bytesUploaded;
            }

            return { uploading };
        });
    };

    private handleUploadSuccess = (file: UppyFileWithKey) => {
        return this.setState(
            (prev: State) => {
                const attachment = prev.uploading.find(i => i.id === file.id);
                if (!attachment) {
                    throw new Error(
                        `Uploader says file ${
                            file.id
                        } finished uploading, but I dont know what that is.`
                    );
                }
                return {
                    uploading: prev.uploading.filter(existing => existing.id != attachment.id),
                    completed: [
                        ...prev.completed,
                        {
                            bucket: attachment.bucket,
                            name: attachment.name,
                            key: attachment.key,
                            previewable: attachment.previewable,
                            size: attachment.size,
                        },
                    ],
                };
            },
            () => {
                this.props.onChange(this.state.completed);
            }
        );
    };

    private handleUploadError = (file: UppyFileWithKey, error: Error) => {
        let message = formatMessage(__('Error uploading {fileName}'), {
            fileName: file.name,
        });

        if (error.message == 'exceedsSize') {
            message = __(
                "Sorry, your file is too big. Please upload it once you've kicked off your project."
            );
        } else if (error.message == 'csrfError') {
            message = __(
                'Sorry, there was an issue uploading your file. Please refresh the page and try again.'
            );
        }
        return this.setState(
            (prev: State) => {
                return {
                    uploading: prev.uploading.filter(a => a.id != file.id),
                    errorMessage: message,
                };
            },
            () => {
                this.props.onChange(this.state.completed);
            }
        );
    };

    private handleUploadDelete = (attachment: UploadingAttachment) => {
        return this.setState((prev: State) => {
            this.uploader.removeFile(attachment.id);
            return {
                uploading: prev.uploading.filter(existing => existing.id != attachment.id),
            };
        });
    };

    private completedDeleted(attachment: Attachment) {
        this.setState(
            (prev: State) => ({
                completed: prev.completed.filter(val => val.key != attachment.key),
            }),
            () => {
                this.props.onChange(this.state.completed);
            }
        );
    }

    private handleAddFiles = (files: File[]) => {
        files.forEach(file => {
            try {
                this.uploader.addFile({
                    name: file.name,
                    type: file.type,
                    data: file,
                });
            } catch {
                // errors handled by uppy internally
            }
        });
    };

    render() {
        return (
            <Dropzone onDrop={this.handleAddFiles}>
                {this.state.errorMessage && <Field errorMessage={this.state.errorMessage} />}

                <Matrix large={3}>
                    {this.state.completed.map(attachment => (
                        <MatrixItem key={attachment.key}>
                            <Attachment
                                name={attachment.name}
                                onDelete={() => this.completedDeleted(attachment)}
                            >
                                <AttachmentPreview
                                    previewable={attachment.previewable}
                                    url={url(attachment)}
                                />
                            </Attachment>
                        </MatrixItem>
                    ))}

                    {this.state.uploading.map(attachment => (
                        <MatrixItem key={attachment.key}>
                            <Attachment
                                name={attachment.name}
                                onDelete={() => this.handleUploadDelete(attachment)}
                            >
                                <UploaderAttachmentProgress
                                    completed={attachment.bytesUploaded}
                                    total={attachment.size}
                                />
                            </Attachment>
                        </MatrixItem>
                    ))}

                    <MatrixItem>
                        <UploaderAttachmentFileInput
                            onClick={(target: HTMLInputElement) => {
                                if (target.files === null) {
                                    return;
                                }
                                this.handleAddFiles(Array.from(target.files));
                            }}
                        >
                            <div className="hidden--small">{__('Drag & drop your files here')}</div>
                            <div className="hidden--medium hidden--normal hidden--large">
                                {__('Upload your files here')}
                            </div>
                        </UploaderAttachmentFileInput>
                    </MatrixItem>
                </Matrix>
            </Dropzone>
        );
    }
}

function url(attachment: Attachment) {
    return `https://${attachment.bucket}.s3-accelerate.amazonaws.com/${attachment.key}`;
}

export default AttachmentUploader;
